import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

export const adminClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
});

const getStoredToken = (): string | null => localStorage.getItem('adminToken');

export const setStoredToken = (token: string): void => {
  localStorage.setItem('adminToken', token);
};

export const removeStoredToken = (): void => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('refreshToken');
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  if (window.location.pathname !== '/admin/login') {
    window.location.replace('/admin/login');
  }
};

adminClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

adminClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const url = originalRequest.url || '';
    if (url.includes('/admin/refresh') || url.includes('/admin/login')) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      removeStoredToken();
      redirectToLogin();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return adminClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const refreshResponse = await adminClient.post('/admin/refresh');
      if (refreshResponse.status === 201 && refreshResponse.data?.accessToken) {
        const newToken = refreshResponse.data.accessToken as string;
        setStoredToken(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return adminClient(originalRequest);
      }
      throw new Error('Не удалось получить новый токен');
    } catch (refreshError) {
      processQueue(refreshError, null);
      removeStoredToken();
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default adminClient;
